import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { MyPageSettingsOverviewScreen } from '../features/my-page-settings/ui/my-page-settings-overview-screen';
import { MyPageSettingsPageLayout } from '../features/my-page-settings/ui/my-page-settings-page-layout';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';

import {
  LogoutConfirmActions,
  LogoutConfirmBackdrop,
  LogoutConfirmCancelButton,
  LogoutConfirmCard,
  LogoutConfirmDescription,
  LogoutConfirmSubmitButton,
  LogoutConfirmTitle,
} from './account-page.styles';

export function AccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearSession } = useEmployeeSession();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  return (
    <MyPageSettingsPageLayout
      backAriaLabel={t('myPage.backAria')}
      frameDataUi="my-page-settings-page-frame"
      onBack={() => {
        navigate('/home');
      }}
      onOpenHome={() => {
        navigate('/home');
      }}
      onOpenMyPage={() => {
        navigate('/my-page');
      }}
      overlay={
        <AccountLogoutConfirmOverlay
          isOpen={isLogoutConfirmOpen}
          onCancel={() => {
            setIsLogoutConfirmOpen(false);
          }}
          onConfirm={() => {
            clearSession();
            navigate('/login');
          }}
        />
      }
      pageDataUi="my-page-settings-page"
      title={t('myPageSettings.title')}
    >
      <MyPageSettingsOverviewScreen
        onLogout={() => {
          setIsLogoutConfirmOpen(true);
        }}
        onOpenLanguage={() => {
          navigate('/settings/language');
        }}
        onOpenPersonalData={() => {
          navigate('/settings/personal-data');
        }}
        onOpenSecurity={() => {
          navigate('/settings/security');
        }}
      />
    </MyPageSettingsPageLayout>
  );
}

type AccountLogoutConfirmOverlayProps = {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

function AccountLogoutConfirmOverlay({
  isOpen,
  onCancel,
  onConfirm,
}: AccountLogoutConfirmOverlayProps) {
  const { t } = useTranslation();

  return (
    <LogoutConfirmBackdrop $isOpen={isOpen} onClick={onCancel}>
      <LogoutConfirmCard
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-logout-confirm-title"
        onClick={event => {
          event.stopPropagation();
        }}
      >
        <LogoutConfirmTitle id="account-logout-confirm-title">
          {t('myPageSettings.logoutConfirm.title')}
        </LogoutConfirmTitle>
        <LogoutConfirmDescription>
          {t('myPageSettings.logoutConfirm.description')}
        </LogoutConfirmDescription>
        <LogoutConfirmActions>
          <LogoutConfirmSubmitButton type="button" onClick={onConfirm}>
            {t('myPageSettings.logoutConfirm.confirm')}
          </LogoutConfirmSubmitButton>
          <LogoutConfirmCancelButton type="button" onClick={onCancel}>
            {t('myPageSettings.logoutConfirm.cancel')}
          </LogoutConfirmCancelButton>
        </LogoutConfirmActions>
      </LogoutConfirmCard>
    </LogoutConfirmBackdrop>
  );
}
